import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { TOUR_STOP } from "../../actions/types";
import OctoIcon from "../../assets/icons/meetocto_icon.svg";
import "./GuidedTour.css";

/* ─── Step definitions ───────────────────────────────────────────────── */

const ADMIN_STEPS = [
  {
    path: "/admin/quick-setup-step-one",
    title: "Set up your organization",
    text: "Fill in your company name, logo, address and phone. Clients see this on every booking page and email.",
    action: "Complete the form below, then come back and click Next.",
  },
  {
    path: "/admin/settings/schedule",
    title: "Set your availability",
    text: "Define your working hours for each day of the week.",
    action: "Configure your hours, then click Next to continue.",
  },
  {
    path: "/admin/events/create/list",
    title: "Create your first event",
    text: "Choose an event type and build your first bookable event — name, duration, location and price.",
    action: "Pick an event type to get started, then return and click Next.",
  },
  {
    path: "/admin/calendar-integration",
    title: "Connect your calendar",
    text: "Sync Google, Outlook or Apple Calendar to avoid double-bookings automatically.",
    action: "Connect at least one calendar, then click Next.",
  },
  {
    path: "/admin/staff/create",
    title: "Invite your team",
    text: "Add staff members so they can manage their own schedules and be assigned to events.",
    action: "Fill in the staff details and save, then click Next.",
  },
  {
    path: "/admin/settings/my-octo-link",
    title: "Personalize your Octo Link",
    text: "Customize your public booking page URL and appearance to match your brand.",
    action: "Update your link settings, then click Next.",
  },
];

const STAFF_STEPS = [
  {
    path: "/staff/profile",
    title: "Complete your profile",
    text: "Add your photo, bio and contact details. Clients see this on your staff booking page.",
    action: "Fill in your profile details and save.",
  },
  {
    path: "/staff/schedule",
    title: "Set your working hours",
    text: "Define the days and times you are available for appointments.",
    action: "Set your hours for each day, then click Next.",
  },
  {
    path: "/staff/calendar",
    title: "Your calendar",
    text: "View all your booked, pending and cancelled appointments in day, week or month view.",
    action: "Explore your calendar — click Next when ready.",
  },
  {
    path: "/staff/booking-history",
    title: "Booking history",
    text: "Review past and upcoming appointments and track client interactions over time.",
    action: "You've completed the staff guide!",
  },
];

/* ─── Helpers ────────────────────────────────────────────────────────── */

const ICONS = ["🏢", "🕐", "📅", "🔗", "👥", "🔑", "🔔", "👤", "📆", "📋"];

/* ─── Component ──────────────────────────────────────────────────────── */

const GuidedTour = () => {
  const { tourActive, tourRole } = useSelector((s) => s.layout);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  locationRef.current = location.pathname;

  const [stepIndex, setStepIndex] = useState(0);
  const [minimized, setMinimized] = useState(false);
  const [finished, setFinished] = useState(false);

  const defs = tourRole === "staff" ? STAFF_STEPS : ADMIN_STEPS;
  const step = defs[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === defs.length - 1;
  const progress = Math.round(((stepIndex + 1) / defs.length) * 100);
  const icon = ICONS[stepIndex] || "💡";

  useEffect(() => {
    if (tourActive) {
      setStepIndex(0);
      setMinimized(false);
      setFinished(false);
      if (defs[0]?.path) {
        navigate(defs[0].path);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourActive, tourRole]);

  const goTo = (idx) => {
    const target = defs[idx];
    if (!target) return;
    setStepIndex(idx);
    setMinimized(false);
    if (target.path && locationRef.current !== target.path) {
      navigate(target.path);
    }
  };

  const handleNext = () => {
    if (isLast) {
      setFinished(true);
      setMinimized(false);
    } else {
      goTo(stepIndex + 1);
    }
  };

  const handleGoToDashboard = () => {
    dispatch({ type: TOUR_STOP });
    setStepIndex(0);
    setFinished(false);
    navigate(tourRole === "staff" ? "/staff/booking-history" : "/admin/dashboard");
  };

  const handleBack = () => {
    if (!isFirst) goTo(stepIndex - 1);
  };

  const handleClose = () => {
    dispatch({ type: TOUR_STOP });
    setStepIndex(0);
  };

  const handleDontShow = () => {
    localStorage.setItem("quickTipsDismissed", "true");
    handleClose();
  };

  if (!tourActive) return null;

  /* ── Congrats screen ── */
  if (finished) {
    return (
      <div className="gt-float">
        <div className="gt-card gt-card--congrats">
          <div className="gt-congrats__fireworks">🎉</div>
          <img src={OctoIcon} alt="MeetOcto" className="gt-congrats__logo" />
          <h2 className="gt-congrats__title">You're all set!</h2>
          <p className="gt-congrats__text">
            Great work — your account is configured and ready to go.
            Start accepting bookings right away.
          </p>
          <button className="gt-congrats__btn" onClick={handleGoToDashboard}>
            Go to Dashboard →
          </button>
          <button className="gt-card__skip" onClick={handleClose} style={{ marginTop: 8 }}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!step) return null;

  return (
    <div className={`gt-float${minimized ? " gt-float--minimized" : ""}`}>
      {minimized ? (
        /* ── Minimized bubble ── */
        <button
          className="gt-float__bubble"
          onClick={() => setMinimized(false)}
          title="Resume guide"
        >
          <img src={OctoIcon} alt="" className="gt-float__bubble-icon" />
          <span className="gt-float__bubble-badge">{stepIndex + 1}</span>
        </button>
      ) : (
        /* ── Expanded card ── */
        <div className="gt-card">
          {/* Header */}
          <div className="gt-card__header">
            <div className="gt-card__brand">
              <img src={OctoIcon} alt="MeetOcto" className="gt-card__logo" />
              <span className="gt-card__label">Guide</span>
            </div>
            <div className="gt-card__controls">
              <button
                className="gt-card__ctrl"
                onClick={() => setMinimized(true)}
                title="Minimize"
              >
                ─
              </button>
              <button
                className="gt-card__ctrl"
                onClick={handleClose}
                title="Close guide"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Progress */}
          <div className="gt-card__progress-wrap">
            <div className="gt-card__progress-bar">
              <div
                className="gt-card__progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="gt-card__progress-label">
              {stepIndex + 1} / {defs.length}
            </span>
          </div>

          {/* Body */}
          <div className="gt-card__body">
            <div className="gt-card__title-row">
              <span className="gt-card__icon">{icon}</span>
              <h3 className="gt-card__title">{step.title}</h3>
            </div>
            <p className="gt-card__text">{step.text}</p>
            <div className="gt-card__action">
              <span className="gt-card__action-dot" />
              {step.action}
            </div>
          </div>

          {/* Step dots */}
          <div className="gt-card__dots">
            {defs.map((_, i) => (
              <button
                key={i}
                className={`gt-card__dot${i === stepIndex ? " gt-card__dot--active" : ""}${i < stepIndex ? " gt-card__dot--done" : ""}`}
                onClick={() => goTo(i)}
                title={`Step ${i + 1}`}
              />
            ))}
          </div>

          {/* Footer */}
          <div className="gt-card__footer">
            <button className="gt-card__skip" onClick={handleDontShow}>
              Don't show again
            </button>
            <div className="gt-card__nav">
              {!isFirst && (
                <button className="gt-card__btn gt-card__btn--back" onClick={handleBack}>
                  ← Back
                </button>
              )}
              <button
                className="gt-card__btn gt-card__btn--next"
                onClick={handleNext}
              >
                {isLast ? "Finish ✓" : "Next →"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidedTour;
